import { useEffect, useRef, useState } from "react";
import { searchCities } from "../../../api/cities";
import type { CityDto } from "../../../types/city";
import { FieldLabel, TextInput } from "../_components/AdminShell";

type CitySourceKeyPickerProps = {
  /** Уже добавленные ключи (id городов) с подписями. */
  keys: Array<{ key: string; label: string }>;
  activeKey: string;
  onSelect: (key: string, label: string) => void;
  onActivate: (key: string) => void;
  onRemove?: (key: string) => void;
};

export function CitySourceKeyPicker({
  keys,
  activeKey,
  onSelect,
  onActivate,
  onRemove,
}: CitySourceKeyPickerProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CityDto[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const knownIds = new Set(keys.map((item) => item.key));

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const cities = await searchCities(trimmed);
        if (requestIdRef.current !== requestId) return;
        setResults(
          cities.slice().sort((a, b) => a.name.localeCompare(b.name, "ru")),
        );
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
        setError(
          err instanceof Error ? err.message : "Не удалось найти города",
        );
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query]);

  return (
    <div className="space-y-2">
      <FieldLabel size="compact">Города (ключи источника)</FieldLabel>

      {keys.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {keys.map((item) => (
            <span
              key={item.key}
              className={[
                "inline-flex items-center gap-1 rounded-md px-2 py-1 font-onest text-[11px] font-medium transition-colors",
                activeKey === item.key
                  ? "bg-brand-100 text-brand-700"
                  : "bg-white text-slate-600 ring-1 ring-slate-200",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => onActivate(item.key)}
                className="max-w-[10rem] truncate"
              >
                {item.label}
              </button>
              {onRemove ? (
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-700"
                  aria-label={`Убрать ${item.label}`}
                  onClick={() => onRemove(item.key)}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}

      <TextInput
        size="compact"
        value={query}
        placeholder="Добавить город — начните вводить название"
        onChange={setQuery}
      />
      <p className="font-onest text-[11px] text-slate-500">
        Можно добавить несколько городов. Для каждого — свой список вариантов.
      </p>

      {isLoading ? (
        <p className="font-onest text-[11px] text-slate-500">Поиск…</p>
      ) : null}
      {error ? (
        <p className="font-onest text-[11px] text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {!isLoading &&
      query.trim().length >= 2 &&
      results.length === 0 &&
      !error ? (
        <p className="font-onest text-[11px] text-slate-500">
          Ничего не найдено
        </p>
      ) : null}

      {results.length > 0 ? (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
          {results.map((city) => {
            const key = String(city.id);
            const already = knownIds.has(key);
            return (
              <li key={key}>
                <button
                  type="button"
                  disabled={already}
                  onClick={() => {
                    onSelect(key, city.name);
                    setQuery("");
                    setResults([]);
                  }}
                  className={[
                    "flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left font-onest text-xs transition-colors",
                    already
                      ? "cursor-default bg-slate-50 text-slate-400"
                      : "text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span className="truncate">{city.name}</span>
                  <span className="shrink-0 font-mono text-[10px] text-slate-400">
                    {already ? "добавлен" : key}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
